import { configureCustomFieldPolicy } from 'library-@/services/custom-field-policy';
import { getCompanyFieldsApi } from 'library-@/apis/workspace_api/custom_field';

interface CustomFieldDefinition {
    field_id?: string;
    field_search_name?: string;
    field_type?: string;
    fieldType?: string;
    customType?: string;
}

interface SearchField extends CustomFieldDefinition {
    fieldId?: string;
    name?: string;
    field_name?: string;
}

export class CustomFieldPolicyService {
    private readonly fields = new Map<string, CustomFieldDefinition>();
    private companyFieldsRequest?: Promise<void>;

    private textCustomFieldEnabled = true;

    updatePermissions(permissions: { enable_text_custom_field?: boolean | null } = {}): void {
        this.textCustomFieldEnabled = permissions.enable_text_custom_field !== false;
    }

    get textCustomFieldDisabled(): boolean {
        return !this.textCustomFieldEnabled;
    }

    registerFields(fields: CustomFieldDefinition[]): void {
        fields.forEach(field => {
            if (field.field_id) this.fields.set(field.field_id, field);
            if (field.field_search_name) this.fields.set(field.field_search_name.toUpperCase(), field);
        });
    }

    isFieldEnabled(field?: CustomFieldDefinition): boolean {
        return !this.textCustomFieldDisabled || (field?.field_type || field?.fieldType || field?.customType) !== 'STRING';
    }

    isFieldIdEnabled(id: string): boolean {
        return this.isFieldEnabled(this.fields.get(id) || this.fields.get(id.toUpperCase()));
    }

    isSearchFieldEnabled(field: SearchField): boolean {
        return this.isFieldEnabled(field) && this.isFieldIdEnabled(field.fieldId || field.field_id || field.name || field.field_name || '');
    }

    filterFields<T extends CustomFieldDefinition>(fields: T[]): T[] {
        this.registerFields(fields);
        return this.textCustomFieldDisabled ? fields.filter(field => this.isFieldEnabled(field)) : fields;
    }

    filterFieldIds(ids: string[]): string[] {
        return this.textCustomFieldDisabled ? ids.filter(id => this.isFieldIdEnabled(id)) : ids;
    }

    async prepareFields(fields: SearchField[]): Promise<void> {
        if (!this.textCustomFieldDisabled || !fields.some(field => {
            if (field.field_type || field.fieldType || field.customType) return false;
            const id = field.fieldId || field.field_id || field.name || field.field_name || '';
            return (field.fieldId || field.field_id || /^CCF_/i.test(id)) && !this.fields.has(id) && !this.fields.has(id.toUpperCase());
        })) return;
        if (!this.companyFieldsRequest) {
            this.companyFieldsRequest = getCompanyFieldsApi().then(() => undefined).catch(error => {
                this.companyFieldsRequest = undefined;
                throw error;
            });
        }
        await this.companyFieldsRequest;
    }

    assertFieldEnabled(field: CustomFieldDefinition): void {
        if (!this.isFieldEnabled(field) || (field.field_id && !this.isFieldIdEnabled(field.field_id))) {
            throw new Error('Text custom fields are disabled');
        }
    }

    // 保存显示设置时保留原始隐藏列，重新启用后仍能恢复。
    preserveHiddenFieldIds(original: string[], visible: string[]): string[] {
        if (!this.textCustomFieldDisabled) return visible;
        const result = this.filterFieldIds(visible);
        const included = new Set(result);
        original.forEach((id, index) => {
            if (!this.isFieldIdEnabled(id) && !included.has(id)) {
                result.splice(Math.min(index, result.length), 0, id);
                included.add(id);
            }
        });
        return result;
    }
}

export const customFieldPolicyService = new CustomFieldPolicyService();
configureCustomFieldPolicy(customFieldPolicyService);
